import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronsUpDown, FolderKanban, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";

export interface PickerProject {
  id: string;
  projectName: string;
  clientName?: string | null;
  thumbnail?: string | null;
}

interface ProjectPickerProps {
  value: string;
  onChange: (projectId: string) => void;
  /** Used to render the current selection when it is not in the recent list
   *  (e.g. an archived project on an existing event). */
  selected?: PickerProject | null;
  disabled?: boolean;
  id?: string;
  testId?: string;
}

/**
 * Searchable project picker shared by every place a project is assigned to a
 * call. The list comes from `/api/calls/recent-projects`, which the server
 * orders by the caller's most recent interaction (same feed as the in-call
 * project chip), and is filtered live on id, name and client. Built on
 * Popover + Command so it nests inside dialogs (Escape / outside click only
 * close the list) and gets combobox keyboard handling for free.
 */
export function ProjectPicker({ value, onChange, selected, disabled, id, testId = "project-picker" }: ProjectPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const { data, isLoading, isError } = useQuery<{ projects: PickerProject[] }>({
    queryKey: ["/api/calls/recent-projects"],
    enabled: open,
    staleTime: 30_000,
  });

  const filtered = useMemo(() => {
    const list = data?.projects ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) =>
      p.projectName.toLowerCase().includes(q) ||
      p.id.toLowerCase().includes(q) ||
      (p.clientName ?? "").toLowerCase().includes(q),
    );
  }, [data, search]);

  const current = value ? (data?.projects.find((p) => p.id === value) ?? selected ?? null) : null;

  const pick = (projectId: string) => {
    onChange(projectId);
    setSearch("");
    setOpen(false);
  };

  const thumb = (p: PickerProject | null, className: string) =>
    p?.thumbnail ? (
      <img src={p.thumbnail} alt="" loading="lazy" decoding="async" className={cn("rounded object-cover shrink-0 bg-gray-800 ring-1 ring-white/10", className)} />
    ) : (
      <FolderKanban className={cn("shrink-0 rounded bg-gray-700 p-1 text-gray-400", className)} />
    );

  return (
    // Deliberately NOT `modal`: a modal popover inside a Radix Dialog disables
    // pointer events on the dialog, so clicking the dialog body closed the whole
    // dialog. Scrolling inside the (portaled) list is kept working by stopping
    // wheel/touch propagation before the dialog's scroll lock sees it.
    <Popover open={open} onOpenChange={(next) => { if (!disabled) { setOpen(next); if (!next) setSearch(""); } }}>
      <div className="flex items-center gap-1 h-8 rounded border border-gray-600 bg-gray-900 pl-2 pr-1 text-xs" data-testid={testId}>
        {thumb(current, "h-6 w-10")}
        <PopoverTrigger asChild>
          <button
            type="button"
            id={id}
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="flex flex-1 min-w-0 items-center gap-1 text-left disabled:opacity-60"
            data-testid={`${testId}-trigger`}
          >
            <span className="flex-1 min-w-0 truncate">
              {current ? `${current.id} · ${current.projectName}` : value ? value : <span className="text-gray-400">No project</span>}
            </span>
            <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-gray-400" />
          </button>
        </PopoverTrigger>
        {value && !disabled && (
          <button type="button" onClick={() => pick("")} aria-label="Clear project" className="p-1 text-gray-400 hover:text-gray-100" data-testid={`${testId}-clear`}>
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <PopoverContent align="start" className="w-[--radix-popover-trigger-width] min-w-[16rem] p-0 bg-gray-900 border-gray-600 text-gray-100">
        <Command shouldFilter={false} className="bg-transparent">
          <CommandInput
            value={search}
            onValueChange={setSearch}
            placeholder="Search project…"
            className="h-8 text-xs"
            data-testid={`${testId}-search`}
          />
          <CommandList className="max-h-[min(22rem,calc(var(--radix-popover-content-available-height)-3rem))] overscroll-contain"
            onWheel={(event) => event.stopPropagation()}
            onTouchMove={(event) => event.stopPropagation()}
            data-testid={`${testId}-list`}
          >
            {isLoading && <div className="px-2 py-2 text-xs text-gray-400">Loading projects…</div>}
            {isError && <div role="alert" className="px-2 py-2 text-xs text-red-300">Projects could not be loaded.</div>}
            {!isLoading && !isError && <CommandEmpty className="py-2 text-xs text-gray-400">No matching project.</CommandEmpty>}
            {filtered.map((p) => (
              <CommandItem
                key={p.id}
                value={p.id}
                onSelect={() => pick(p.id)}
                className={cn("gap-2 text-xs aria-selected:bg-gray-700", p.id === value && "bg-gray-700/60")}
                data-testid={`${testId}-option-${p.id}`}
              >
                {thumb(p, "h-7 w-12")}
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{p.id} · {p.projectName}</div>
                  {p.clientName && <div className="truncate text-[10px] text-gray-400">{p.clientName}</div>}
                </div>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
